<template>
    <div class="claimants-page w-full max-w-full pb-10">
        
        <div class="mt-6 sm:mt-10 px-4 w-full max-w-7xl mx-auto">

            <div class="flex flex-col gap-3 sm:gap-4 xl:flex-row xl:justify-between xl:items-center">
                <div class="flex items-center justify-between gap-3 xl:justify-start">
                    <div class="flex items-baseline gap-2 min-w-0">
                        <h2 class="tracking-wide font-semibold text-xl sm:text-2xl text-warm-gray-700 dark:text-warm-gray-50 truncate">Reward Claimants</h2>
                        <span class="text-sm text-warm-gray-500 dark:text-warm-gray-400 shrink-0">{{ displayedCount }} shown<span v-if="totalClaimants !== null"> · {{ totalClaimants.toLocaleString() }} total</span></span>
                    </div>
                    <button @click="reloadCurrentTab()" class="xl:hidden shrink-0 rounded-full bg-warm-gray-200 text-warm-gray-500 hover:bg-warm-gray-300 dark:bg-warm-gray-800 dark:text-warm-gray-600 dark:hover:bg-warm-gray-700 p-1.5" :disabled="isFetching || isLoadingMore">
                        <ArrowPathIcon class=" w-5 h-5" />
                    </button>
                </div>
                <div class="flex items-center gap-2 w-full xl:w-auto min-w-0">
                    <div class="flex flex-wrap w-full rounded-md overflow-hidden border border-warm-gray-600 bg-warm-gray-700">
                        <button @click="switchTab('pendings')" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == 'pendings' && 'bg-warm-gray-800'">Pending</button>
                        <button @click="switchTab('rejected')" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == 'rejected' && 'bg-warm-gray-800'">Rejected</button>
                        <button @click="switchTab('approved')" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == 'approved' && 'bg-warm-gray-800'">Approved</button>
                        <button @click="switchTab('shipped')" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == 'shipped' && 'bg-warm-gray-800'">Shipped</button>
                        <button @click="switchTab('forfeited')" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == 'forfeited' && 'bg-warm-gray-800'">Forfeited</button>
                        <button @click="switchTab(null)" class="py-1.5 px-3 text-sm whitespace-nowrap hover:bg-warm-gray-800" :class="currentTab == null && 'bg-warm-gray-800'">All</button>
                    </div>
                    <button @click="reloadCurrentTab()" class="hidden xl:inline-flex shrink-0 rounded-full bg-warm-gray-200 text-warm-gray-500 hover:bg-warm-gray-300 dark:bg-warm-gray-800 dark:text-warm-gray-600 dark:hover:bg-warm-gray-700 p-1.5" :disabled="isFetching || isLoadingMore">
                        <ArrowPathIcon class=" w-5 h-5" />
                    </button>
                </div>
            </div>

            <div class="mt-4 flex flex-col gap-2">
                <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <input
                        v-model="emailQuery"
                        type="search"
                        placeholder="Search email"
                        class="w-full flex-1 h-9 px-3 rounded border border-warm-gray-400 bg-white text-sm text-warm-gray-800 focus:outline-none focus:border-yellow-500 dark:bg-warm-gray-800 dark:text-warm-gray-200 dark:border-warm-gray-600"
                        @keydown.enter.prevent="searchEmail"
                    />
                    <div class="flex gap-2 shrink-0">
                    <button
                        type="button"
                        class="flex-1 sm:flex-none h-9 px-3 rounded border border-warm-gray-400 text-sm text-warm-gray-700 hover:bg-warm-gray-100 dark:text-warm-gray-200 dark:border-warm-gray-600 dark:hover:bg-warm-gray-800"
                        :disabled="isSearchingEmail"
                        @click="searchEmail"
                    >
                        {{ isSearchingEmail ? 'Searching…' : 'Search' }}
                    </button>
                    <button
                        type="button"
                        class="flex-1 sm:flex-none h-9 px-3 rounded border border-warm-gray-400 text-sm text-warm-gray-700 hover:bg-warm-gray-100 dark:text-warm-gray-200 dark:border-warm-gray-600 dark:hover:bg-warm-gray-800"
                        @click="showAdvanced = !showAdvanced"
                    >
                        {{ showAdvanced ? 'Hide filters' : 'Advanced' }}
                    </button>
                    </div>
                </div>
                <div v-if="showAdvanced" class="flex flex-col sm:flex-row gap-2">
                    <input
                        v-model="nameFilter"
                        type="search"
                        placeholder="Filter name (loaded list)"
                        class="flex-1 h-9 px-3 rounded border border-warm-gray-400 bg-white text-sm text-warm-gray-800 focus:outline-none focus:border-yellow-500 dark:bg-warm-gray-800 dark:text-warm-gray-200 dark:border-warm-gray-600"
                    />
                    <input
                        v-model="schoolFilter"
                        type="search"
                        placeholder="Filter school (loaded list)"
                        class="flex-1 h-9 px-3 rounded border border-warm-gray-400 bg-white text-sm text-warm-gray-800 focus:outline-none focus:border-yellow-500 dark:bg-warm-gray-800 dark:text-warm-gray-200 dark:border-warm-gray-600"
                    />
                </div>
                <p v-if="emailLookupActive" class="text-xs text-warm-gray-500 dark:text-warm-gray-400">
                    Showing email match from Firestore. Clear the search box to return to this tab.
                </p>
                <p v-if="emailLookupEmpty" class="text-xs text-red-600 dark:text-red-400">
                    No claimant found with that email.
                </p>
            </div>

            <div v-if="isFetching && !loadedClaimants.length" class="mt-20 text-center text-warm-gray-500 italic">Fetching data... Please wait.</div>

            <div v-else class="claimants-table-scroll mt-5">
            <table class="w-full min-w-[1100px] text-sm">
                <thead>
                    <tr class="bg-warm-gray-300 border-warm-gray-500 text-warm-gray-600 dark:text-warm-gray-300 dark:bg-warm-gray-700 dark:border-warm-gray-300 border-b-2 border-t-2 ">
                        <th class="text-left py-2 pl-4">Name</th>
                        <th class="text-left">Email</th>
                        <th class="text-center">
                            <button type="button" class="inline-flex items-center gap-0.5" @click="toggleSort('school')">
                                School
                                <ChevronUpDownIcon v-if="sortKey !== 'school'" class="w-3.5 h-3.5 opacity-50" />
                                <ChevronUpIcon v-else-if="sortDir === 'asc'" class="w-3.5 h-3.5" />
                                <ChevronDownIcon v-else class="w-3.5 h-3.5" />
                            </button>
                        </th>
                        <th class="text-center">PES</th>
                        <th class="text-center">ABox</th>
                        <th class="text-center">Fee</th>
                        <th class="text-center">
                            <button type="button" class="inline-flex items-center gap-0.5" @click="toggleSort('postDate')">
                                Social Post
                                <ChevronUpDownIcon v-if="sortKey !== 'postDate'" class="w-3.5 h-3.5 opacity-50" />
                                <ChevronUpIcon v-else-if="sortDir === 'asc'" class="w-3.5 h-3.5" />
                                <ChevronDownIcon v-else class="w-3.5 h-3.5" />
                            </button>
                        </th>
                        <th class="text-center">
                            <button type="button" class="inline-flex items-center gap-0.5" @click="toggleSort('updatedOn')">
                                Updated On
                                <ChevronUpDownIcon v-if="sortKey !== 'updatedOn'" class="w-3.5 h-3.5 opacity-50" />
                                <ChevronUpIcon v-else-if="sortDir === 'asc'" class="w-3.5 h-3.5" />
                                <ChevronDownIcon v-else class="w-3.5 h-3.5" />
                            </button>
                        </th>
                        <th class="text-center">
                            <button type="button" class="inline-flex items-center gap-0.5" @click="toggleSort('aboxApprovedOn')">
                                ABox Approved
                                <ChevronUpDownIcon v-if="sortKey !== 'aboxApprovedOn'" class="w-3.5 h-3.5 opacity-50" />
                                <ChevronUpIcon v-else-if="sortDir === 'asc'" class="w-3.5 h-3.5" />
                                <ChevronDownIcon v-else class="w-3.5 h-3.5" />
                            </button>
                        </th>
                        <th class=""></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="displayedClaimants.length === 0 && !isFetching">
                        <td colspan="10" class="text-center py-8 text-warm-gray-500">No claimants match this view.</td>
                    </tr>
                    <tr v-for="claimant in displayedClaimants" :key="claimant.id" class="border-b border-dashes border-warm-gray-600 hover:bg-warm-gray-100 dark:border-warm-gray-600 dark:hover:bg-warm-gray-800">
                        <td class="text-left pl-4 py-1.5 whitespace-nowrap">{{ claimant.fullName || '---' }}</td>
                        <td class="text-left py-1.5 whitespace-nowrap">{{ claimant.data.email }}</td>
                        <td class="text-center whitespace-nowrap">{{ claimant.schoolShorthand || '---' }}</td>
                        <td class="text-center whitespace-nowrap">
                            <span class="rounded-full text-white text-xs px-2 py-0.5" :class="statusPillClass(claimant.data.postExamSurveyInfoStatus)">
                                {{ claimant.data.postExamSurveyInfoStatus }}
                            </span>
                        </td>
                        <td class="text-center whitespace-nowrap">
                            <span class="rounded-full text-white text-xs px-2 py-0.5" :class="statusPillClass(claimant.data.achievementBoxInfoStatus)">
                                {{ claimant.data.achievementBoxInfoStatus }}
                            </span>
                            <span v-if="claimant.data.isQueued" class="ml-1 inline-flex items-center rounded-md bg-yellow-400 bg-opacity-10 px-1.5 py-0.5 text-xs font-medium text-yellow-600 ring-1 ring-inset ring-yellow-400 ring-opacity-30 dark:text-yellow-400">Queued</span>
                        </td>
                        <td class="text-center text-sm whitespace-nowrap">{{ formatShippingFee(claimant.data.shippingFee) }}</td>
                        <td class="text-center whitespace-nowrap">
                            <span>{{ claimant.data.isHoldPost ? 'HOLD' : claimant.data.facebookPostDate ? formatDate(claimant.data.facebookPostDate) : '--' }}</span>
                            <span v-if="claimant.data.isSpecialPost" class="ml-1 inline-flex items-center rounded-md bg-yellow-400 bg-opacity-10 px-1.5 py-0.5 text-xs font-medium text-yellow-600 ring-1 ring-inset ring-yellow-400 ring-opacity-30 dark:text-yellow-400">SP</span>
                        </td>
                        <td class="text-center whitespace-nowrap">{{ formatDate(claimant.data.metadata && claimant.data.metadata.updatedOn)  }}</td>
                        <td class="text-center whitespace-nowrap">{{ claimant.aboxApprovedOn ? formatDate(claimant.aboxApprovedOn) : '---' }}</td>
                        <td class="pr-4 whitespace-nowrap">
                            <router-link
                                :to="'/reward/view/' + claimant.id"
                                class="border border-yellow-600 px-1 text-yellow-600 rounded"
                                @click="persistListState({ resumeTab: true })"
                            >view</router-link>
                        </td>
                    </tr>
                </tbody>
            </table>
            </div>

            <div v-if="!emailLookupActive" class="mt-4 flex justify-center">
                <button
                    v-if="hasMore && (!isFetching || loadedClaimants.length)"
                    type="button"
                    class="px-4 py-2 rounded border border-warm-gray-500 text-sm text-warm-gray-600 hover:bg-warm-gray-100 disabled:opacity-40 dark:text-warm-gray-300 dark:border-warm-gray-500 dark:hover:bg-warm-gray-800"
                    :disabled="isFetching || isLoadingMore"
                    @click="loadMore"
                >
                    {{ isLoadingMore ? 'Loading…' : 'Load more' }}
                </button>
            </div>
        </div>

    </div>
</template>

<script setup>

import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue'
import {fetchClaimantsPage, fetchClaimantsByEmail, fetchClaimantListExtras, getClaimantsTabCache, setClaimantsTabCache, subscribeClaimantsRevision, fetchClaimantsTotalCount} from '../firebase/index'
import {ArrowPathIcon, ChevronDownIcon, ChevronUpIcon, ChevronUpDownIcon} from '@heroicons/vue/24/outline'

const LIST_STATE_KEY = 'admin.rewardClaimants.v2'
const RESUME_TAB_KEY = 'admin.rewardClaimants.resumeTab'

const isFetching = ref(false);
const isLoadingMore = ref(false);
const isSearchingEmail = ref(false);
const loadedClaimants = ref([]);
const lookupClaimants = ref([]);
const currentTab = ref('pendings');
const emailQuery = ref('');
const nameFilter = ref('');
const schoolFilter = ref('');
const showAdvanced = ref(false);
const sortKey = ref('aboxApprovedOn');
const sortDir = ref('desc');
const pageCursor = ref(null);
const pageCursors = ref(null);
const hasMore = ref(false);
const emailLookupActive = ref(false);
const emailLookupEmpty = ref(false);
const cachedSchools = ref(null)
const totalClaimants = ref(null)
let unsubClaimantsRevision = null;

const sourceClaimants = computed(() => {
    return emailLookupActive.value ? lookupClaimants.value : loadedClaimants.value
})

const displayedClaimants = computed(() => {
    const email = emailQuery.value.trim().toLowerCase()
    const name = nameFilter.value.trim().toLowerCase()
    const school = schoolFilter.value.trim().toLowerCase()
    let rows = sourceClaimants.value
    if (email && !emailLookupActive.value) {
        rows = rows.filter((claimant) => String((claimant.data && claimant.data.email) || '').toLowerCase().includes(email))
    }
    if (name) {
        rows = rows.filter((claimant) => String(claimant.fullName || '').toLowerCase().includes(name))
    }
    if (school) {
        rows = rows.filter((claimant) => {
            const haystack = [claimant.schoolShorthand, claimant.schoolName].join(' ').toLowerCase()
            return haystack.includes(school)
        })
    }
    const dir = sortDir.value === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => compareClaimants(a, b, sortKey.value) * dir)
})

const displayedCount = computed(() => displayedClaimants.value.length)

const refreshTotalCount = async ({ force } = {}) => {
    try {
        totalClaimants.value = await fetchClaimantsTotalCount({ force })
    } catch (error) {
        console.error('Error fetching claimants total count', error)
    }
}

watch(emailQuery, (value) => {
    if (!String(value || '').trim()) {
        emailLookupActive.value = false
        emailLookupEmpty.value = false
        lookupClaimants.value = []
    }
})

const normalizeTab = (tab) => {
    if (tab === 'returned' || tab === 'received') return 'shipped'
    if (tab === undefined) return 'pendings'
    return tab
}

const toggleSort = (key) => {
    if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
        return
    }
    sortKey.value = key
    sortDir.value = key === 'school' ? 'asc' : 'desc'
}

const enrichClaimant = (row, extras) => {
    const data = row.data || {}
    const user = (extras.usersById || {})[row.id] || {}
    const abox = (extras.aboxById || {})[row.id] || {}
    const schoolId = data.schoolId || user.school
    const school = extras.schools[schoolId] || {}
    const stampedName = (data.fullName || '').trim()
    const joinedName = [user.firstname, user.lastname].filter(Boolean).join(' ').trim()
    const stampedSchool = (data.schoolShorthand || '').trim()
    const aboxApprovedOn = data.achievementBoxApprovedOn || (abox.metadata && abox.metadata.approvedOn) || ''
    return {
        ...row,
        fullName: stampedName || joinedName,
        schoolShorthand: stampedSchool || school.shorthand || '',
        schoolName: school.name || '',
        aboxApprovedOn: aboxApprovedOn,
    }
}

const appendEnriched = async (rows, reset) => {
    const extras = await fetchClaimantListExtras(rows, cachedSchools.value)
    cachedSchools.value = extras.schools
    const next = rows.map((row) => enrichClaimant(row, extras))
    if (reset) {
        loadedClaimants.value = next
        return
    }
    const existing = {}
    loadedClaimants.value.forEach((row) => { existing[row.id] = true })
    loadedClaimants.value = loadedClaimants.value.concat(next.filter((row) => !existing[row.id]))
}

const loadTab = async (tab, { reset } = { reset: true }) => {
    const nextTab = normalizeTab(tab)
    if (reset) {
        isFetching.value = true
        currentTab.value = nextTab
        pageCursor.value = null
        pageCursors.value = null
        hasMore.value = false
        loadedClaimants.value = []
        emailLookupActive.value = false
        emailLookupEmpty.value = false
        lookupClaimants.value = []
    }
    try {
        const page = await fetchClaimantsPage({
            tab: nextTab,
            cursor: reset ? null : pageCursor.value,
            cursors: reset ? null : pageCursors.value,
            seenIds: reset ? [] : loadedClaimants.value.map((row) => row.id),
        })
        await appendEnriched(page.rows, reset)
        pageCursor.value = page.cursor
        pageCursors.value = page.cursors
        hasMore.value = page.hasMore
        rememberCurrentTab()
    } catch (error) {
        console.error('Error fetching claimants page', error)
    } finally {
        isFetching.value = false
        isLoadingMore.value = false
    }
}

const rememberCurrentTab = () => {
    setClaimantsTabCache(currentTab.value, {
        rows: loadedClaimants.value,
        cursor: pageCursor.value,
        cursors: pageCursors.value,
        hasMore: hasMore.value,
    })
}

const applyTabCache = (cached) => {
    loadedClaimants.value = cached.rows || []
    pageCursor.value = cached.cursor || null
    pageCursors.value = cached.cursors || null
    hasMore.value = !!cached.hasMore
    isFetching.value = false
    isLoadingMore.value = false
}

const switchTab = async (tab) => {
    if (isFetching.value || isLoadingMore.value) return
    const nextTab = normalizeTab(tab)
    if (nextTab === currentTab.value) return
    if (getClaimantsTabCache(currentTab.value) || loadedClaimants.value.length) {
        rememberCurrentTab()
    }
    emailQuery.value = ''
    emailLookupActive.value = false
    emailLookupEmpty.value = false
    lookupClaimants.value = []
    currentTab.value = nextTab
    const cached = getClaimantsTabCache(nextTab)
    if (cached) {
        applyTabCache(cached)
        return
    }
    await loadTab(nextTab, { reset: true })
}

const reloadCurrentTab = async () => {
    if (isFetching.value || isLoadingMore.value) return
    refreshTotalCount({ force: true })
    await loadTab(currentTab.value, { reset: true })
}

const loadMore = async () => {
    if (!hasMore.value || isFetching.value || isLoadingMore.value || emailLookupActive.value) return
    isLoadingMore.value = true
    await loadTab(currentTab.value, { reset: false })
}

const searchEmail = async () => {
    const q = emailQuery.value.trim().toLowerCase()
    emailLookupEmpty.value = false
    if (!q) {
        emailLookupActive.value = false
        lookupClaimants.value = []
        return
    }
    const local = loadedClaimants.value.filter((claimant) => String((claimant.data && claimant.data.email) || '').toLowerCase().includes(q))
    if (local.length) {
        emailLookupActive.value = false
        lookupClaimants.value = []
        return
    }
    isSearchingEmail.value = true
    try {
        const rows = await fetchClaimantsByEmail(emailQuery.value)
        if (!rows.length) {
            emailLookupActive.value = false
            emailLookupEmpty.value = true
            lookupClaimants.value = []
            return
        }
        const extras = await fetchClaimantListExtras(rows, cachedSchools.value)
        cachedSchools.value = extras.schools
        lookupClaimants.value = rows.map((row) => enrichClaimant(row, extras))
        emailLookupActive.value = true
    } catch (error) {
        console.error('Error searching claimant email', error)
        emailLookupEmpty.value = true
        emailLookupActive.value = false
        lookupClaimants.value = []
    } finally {
        isSearchingEmail.value = false
    }
}

const compareClaimants = (a, b, key) => {
    const asString = (value) => (value || '').toString().toLowerCase()
    const asNumber = (value) => {
        const n = parseInt(value, 10)
        return Number.isNaN(n) ? 0 : n
    }
    switch (key) {
        case 'school':
            return asString(a.schoolShorthand).localeCompare(asString(b.schoolShorthand))
        case 'postDate':
            return asNumber(a.data.facebookPostDate) - asNumber(b.data.facebookPostDate)
        case 'aboxApprovedOn':
            return asNumber(a.aboxApprovedOn) - asNumber(b.aboxApprovedOn)
        case 'updatedOn':
        default:
            return asNumber(a.data.metadata && a.data.metadata.updatedOn) - asNumber(b.data.metadata && a.data.metadata.updatedOn)
    }
}

const statusPillClass = (status) => {
    if (status == 'submitted') return 'bg-warm-gray-700'
    if (status == 'rejected') return 'bg-red-700'
    if (status == 'approved') return 'bg-green-700'
    if (status == 'shipped') return 'bg-indigo-500'
    if (status == 'returned') return 'bg-yellow-900'
    return 'bg-yellow-700'
}

const persistListState = ({ resumeTab } = {}) => {
    const state = {
        tab: currentTab.value,
        email: emailQuery.value,
        name: nameFilter.value,
        school: schoolFilter.value,
        showAdvanced: showAdvanced.value,
        sortKey: sortKey.value,
        sortDir: sortDir.value,
        scrollY: window.scrollY,
    }
    sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(state))
    if (resumeTab) sessionStorage.setItem(RESUME_TAB_KEY, '1')
}

const restoreListState = () => {
    try {
        const raw = sessionStorage.getItem(LIST_STATE_KEY)
        if (!raw) return null
        return JSON.parse(raw)
    } catch (err) {
        return null
    }
}

onMounted(async ()=>{
    const saved = restoreListState()
    if (saved) {
        emailQuery.value = saved.email || saved.search || ''
        nameFilter.value = saved.name || ''
        schoolFilter.value = saved.school || ''
        showAdvanced.value = !!saved.showAdvanced
        sortKey.value = saved.sortKey || 'aboxApprovedOn'
        sortDir.value = saved.sortDir || 'desc'
        if (saved.sort === 'aboxApprovedOn:asc' || saved.sort === 'updatedOn:asc' || saved.sort === 'postDate:asc' || saved.sort === 'school:asc') {
            sortDir.value = 'asc'
        }
    }
    const resumeTab = sessionStorage.getItem(RESUME_TAB_KEY) === '1'
    sessionStorage.removeItem(RESUME_TAB_KEY)
    const initialTab = resumeTab
        ? normalizeTab(saved && saved.tab !== undefined ? saved.tab : 'pendings')
        : 'pendings'
    currentTab.value = initialTab
    unsubClaimantsRevision = subscribeClaimantsRevision(() => {
        refreshTotalCount({ force: true })
        if (isFetching.value || isLoadingMore.value) return
        loadTab(currentTab.value, { reset: true })
    })
    refreshTotalCount()
    const cached = getClaimantsTabCache(initialTab)
    if (cached) {
        applyTabCache(cached)
    } else {
        await loadTab(initialTab, { reset: true })
    }
    await nextTick()
    if (resumeTab && saved && typeof saved.scrollY === 'number') {
        window.scrollTo(0, saved.scrollY)
    }
})

onBeforeUnmount(() => {
    persistListState()
    if (unsubClaimantsRevision) unsubClaimantsRevision()
})


const formatDate = (timestampInSeconds) => {
    if (timestampInSeconds === null || timestampInSeconds === undefined || timestampInSeconds === '') return '---'

    const date = new Date( parseInt(timestampInSeconds));

    const months = [
    "Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.",
    "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;

}

const formatShippingFee = (fee) => {
    if (fee === 0 || fee === '0') return '₱0.00'
    if (fee === null || fee === undefined || fee === '') return '---'
    const amount = Number(fee)
    if (!Number.isFinite(amount)) return '---'
    return `₱${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}


</script>

<style scoped>
.claimants-page {
  max-width: 100%;
  overflow-x: hidden;
}
.claimants-table-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
</style>

